// Lib
import React, { MutableRefObject, RefObject, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

// Utils
import { createInertiaScrollDetector } from '../../../../../../canvas/zoom/inertiaScrollDetector';
import { createWheelModeTracker } from '../../../../../../canvas/zoom/wheelZoomInteractionUtils';
import { createPostZoomWheelScrollGuard, processImageModalWheelEvent } from './imageModalWheelInteractionUtils';
import { useImageModalV2ViewportGeometry } from './useImageModalV2ViewportGeometry';
import { useImageModalV2WindowPanListeners } from './useImageModalV2WindowPanListeners';

// Selectors
import { getPlatformDetailsSelector } from '../../../../../../platform/platformSelector';

// Types
import { UseImageModalZoomResult } from '../../../hooks/useImageModalZoom';

type UseImageModalV2ZoomInteractionsResult = {
    zoomViewportRef: MutableRefObject<HTMLDivElement | null>;
    panElementRef: MutableRefObject<HTMLDivElement | null>;
    frameElementRef: MutableRefObject<HTMLDivElement | null>;
    onImageMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
};

/**
 * Connects the V2 image content DOM events to the shared image zoom state.
 */
export const useImageModalV2ZoomInteractions = (
    imageZoom: UseImageModalZoomResult,
    imageAreaRef: RefObject<HTMLDivElement>,
): UseImageModalV2ZoomInteractionsResult => {
    const panElementRef = useRef<HTMLDivElement | null>(null);
    const frameElementRef = useRef<HTMLDivElement | null>(null);
    const wheelModeTrackerRef = useRef(createWheelModeTracker());
    const inertiaScrollDetectorRef = useRef(createInertiaScrollDetector());
    const postZoomWheelScrollGuardRef = useRef(createPostZoomWheelScrollGuard());
    const platformDetails = useSelector(getPlatformDetailsSelector);
    const {
        endPan,
        isPanning,
        isZoomed,
        panBy,
        recordZoomAnchorPoint,
        registerZoomTransformElements,
        setViewportSize,
        startPan,
        updatePan,
        zoomWheelToPoint,
    } = imageZoom;

    const { getMeasuredViewportCentrePoint, getViewportPoint, zoomViewportRef } =
        useImageModalV2ViewportGeometry(setViewportSize);

    /**
     * Adapts non-passive wheel events into the shared image zoom wheel interaction helper.
     */
    const onImageWheel = useCallback(
        (event: WheelEvent) => {
            processImageModalWheelEvent(
                event,
                {
                    wheelModeTracker: wheelModeTrackerRef.current,
                    inertiaScrollDetector: inertiaScrollDetectorRef.current,
                    postZoomWheelScrollGuard: postZoomWheelScrollGuardRef.current,
                },
                {
                    getViewportCentrePoint: getMeasuredViewportCentrePoint,
                    getViewportPoint,
                    isZoomed,
                    panBy,
                    recordZoomAnchorPoint,
                    zoomWheelToPoint,
                },
                { platformDetails },
            );
        },
        [
            getMeasuredViewportCentrePoint,
            getViewportPoint,
            isZoomed,
            panBy,
            platformDetails,
            recordZoomAnchorPoint,
            zoomWheelToPoint,
        ],
    );

    /**
     * Starts image drag panning on primary-button mouse down while zoomed.
     */
    const onImageMouseDown = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (event.button !== 0 || !isZoomed) return;

            const didStartPan = startPan({ x: event.clientX, y: event.clientY });
            if (!didStartPan) return;

            event.preventDefault();
            event.stopPropagation();
        },
        [isZoomed, startPan],
    );

    /**
     * Registers the DOM elements that receive the split pan and scale transforms.
     */
    useEffect(() => {
        registerZoomTransformElements({
            panElement: panElementRef.current,
            frameElement: frameElementRef.current,
        });

        return () => {
            registerZoomTransformElements({
                panElement: null,
                frameElement: null,
            });
        };
    }, [registerZoomTransformElements]);

    /**
     * Binds the wheel listener manually so it can prevent the browser's passive default handling.
     */
    useEffect(() => {
        const imageAreaElement = imageAreaRef.current;
        if (!imageAreaElement) return;

        const abortController = new AbortController();

        imageAreaElement.addEventListener('wheel', onImageWheel, {
            passive: false,
            signal: abortController.signal,
        });

        return () => {
            abortController.abort();
        };
    }, [imageAreaRef, onImageWheel]);

    useImageModalV2WindowPanListeners({ endPan, isPanning, updatePan });

    /**
     * Disposes scheduled wheel mode resets on unmount.
     */
    useEffect(() => {
        return () => {
            wheelModeTrackerRef.current.dispose();
        };
    }, []);

    return {
        zoomViewportRef,
        panElementRef,
        frameElementRef,
        onImageMouseDown,
    };
};
